import { Editor } from "grapesjs";

export const addProperties = (editor: Editor) => {
  const styleManager = editor.StyleManager;

  const fontProperty = styleManager.getProperty("typography", "font-family");
  if (fontProperty) {
    fontProperty.set("default", "");
    // @ts-ignore
    const options = fontProperty.get("options");
    options.unshift({ id: "", name: "Not Selected" });
    // @ts-ignore
    fontProperty.set("options", options);
  }

  styleManager.addProperty(
    "dimension",
    {
      label: "Minimum width",
      property: "min-width",
      type: "number",
      units: ["px", "%"],
    },
    { at: 0 }
  );

  styleManager.addProperty(
    "extra",
    {
      label: "overflow",
      property: "overflow",
      type: "select",
      options: [
        { id: "visible", label: "visible" },
        { id: "hidden", label: "hidden" },
        { id: "scroll", label: "scroll" },
        { id: "auto", label: "auto" },
        { id: "clip", label: "clip" },
      ],
    },
    { at: 0 }
  );
};

export const addTraits = (editor: Editor) => {
  editor.DomComponents.addType("video", {
    model: {
      defaults: {
        attributes: { muted: true, playsinline: true },
      },
    },
  });

  // Extend the image type with custom traits and logic for dynamic src
  editor.DomComponents.addType("image", {
    model: {
      defaults: {
        ...editor.DomComponents.getType("image")!.model.prototype.defaults,
        attributes: {
          ...editor.DomComponents.getType("image")!.model.prototype.defaults
            .attributes,
          alt: "",
        },
        traits: [
          ...editor.DomComponents.getType(
            "image"
          )!.model.prototype.defaults.traits.filter(
            (t: { name: string }) => t.name !== "src"
          ),
          {
            type: "number",
            name: "imgwidth",
            label: "Resize Width",
            min: 0,
            changeProp: true,
          },
          {
            type: "number",
            name: "imgheight",
            label: "Resize Height",
            min: 0,
            changeProp: true,
          },
          {
            type: "checkbox",
            name: "imgcrop",
            label: "Crop",
            changeProp: true,
          },
        ],
      },
      initialize() {
        try {
          const imageUrl = new URL(this.get("src"));
          const params = imageUrl.searchParams;
          const w = params.get("width");
          if (w) {
            this.getTrait("imgwidth").setValue(parseInt(w, 10));
            params.delete("width");
          }
          const h = params.get("height");
          if (h) {
            this.getTrait("imgheight").setValue(parseInt(h, 10));
            params.delete("height");
          }
          const crop = params.get("crop");
          if (crop === "true") {
            this.getTrait("imgcrop").setValue(true);
            params.delete("crop");
          }
          this.set("src", imageUrl.toString());
        } catch (e) {

        }
        this.listenTo(
          this,
          "change:imgwidth change:imgheight change:imgcrop",
          this.updateSrc
        );
        this.updateSrc();
      },
      updateSrc() {
        try {
          const url = new URL(this.get("src"));
          const params = url.searchParams;
          params.delete("width");
          params.delete("height");
          params.delete("crop");
          const w = this.getTrait("imgwidth")?.get("value");
          if (w && w > 0) params.set("width", w);
          const h = this.getTrait("imgheight")?.get("value");
          if (h && h > 0) params.set("height", h);
          if (this.getTrait("imgcrop")?.get("value"))
            params.set("crop", "true");
          this.set("src", url.toString());
        } catch (e) {}
      },
    },
  });

  editor.Traits.addType("custom-attributes", {
    templateInput: () => "",
    noLabel: true,
    createInput({ component }) {
      const container = document.createElement("div");
      container.classList.add(
        "d-flex",
        "flex-column",
        "justify-content-center"
      );

      const addButton = document.createElement("button");
      addButton.innerText = "Add Attribute";
      addButton.type = "button";
      addButton.classList.add("btn", "btn-primary", "btn-sm", "mb-2");
      container.appendChild(addButton);

      type CustomAttribute = { key: string; value: string };

      const updateAttributes = (): CustomAttribute[] => {
        const data: CustomAttribute[] = [];
        container.querySelectorAll(".custom-attr-row").forEach((row) => {
          const key = (
            row.querySelector(".attr-key") as HTMLInputElement
          )?.value.trim();
          const value = (
            row.querySelector(".attr-value") as HTMLInputElement
          )?.value.trim();
          if (key) data.push({ key, value });
        });
        return data;
      };

      const saveAttributes = (oldKey?: string) => {
        const attributes = updateAttributes();
        component.set("customAttributes", attributes);
        let existingAttributes = component.getAttributes();

        if (oldKey && oldKey in existingAttributes) {
          delete existingAttributes[oldKey];
        }

        const attributeEntries = Object.fromEntries(
          attributes.map(({ key, value }) => [key, value])
        );
        component.setAttributes({ ...existingAttributes, ...attributeEntries });
      };

      const addAttributeRow = (key = "", value = "") => {
        const row = document.createElement("div");
        row.classList.add("custom-attr-row", "border-bottom", "mb-3", "pb-2");

        const keyInput = document.createElement("input");
        keyInput.type = "text";
        keyInput.classList.add(
          "form-control-sm",
          "attr-key",
          "mb-1",
          "form-control"
        );
        keyInput.placeholder = "Attribute name";
        keyInput.value = key;

        const valueInput = document.createElement("input");
        valueInput.type = "text";
        valueInput.classList.add(
          "form-control-sm",
          "attr-value",
          "form-control"
        );
        valueInput.placeholder = "Value";
        valueInput.value = value;

        const removeButton = document.createElement("button");
        removeButton.innerText = "Remove";
        removeButton.type = "button";
        removeButton.classList.add(
          "btn",
          "btn-sm",
          "btn-outline-danger",
          "my-1"
        );
        removeButton.addEventListener("click", () => {
          row.remove();
          saveAttributes(keyInput.value.trim());
        });

        let oldKey = key;
        keyInput.addEventListener(
          "focus",
          () => (oldKey = keyInput.value.trim())
        );
        keyInput.addEventListener("blur", () => saveAttributes(oldKey));
        valueInput.addEventListener("blur", () => saveAttributes());

        row.appendChild(keyInput);
        row.appendChild(valueInput);
        row.appendChild(removeButton);
        container.appendChild(row);
      };

      const renderAttributes = (attributes: CustomAttribute[] = []) => {
        container
          .querySelectorAll(".custom-attr-row")
          .forEach((row) => row.remove());
        attributes.forEach(({ key, value }) => addAttributeRow(key, value));
      };

      const savedAttributes: CustomAttribute[] =
        component.get("customAttributes") || [];
      renderAttributes(savedAttributes);

      addButton.addEventListener("click", () => addAttributeRow());
      (container as any).renderAttributes = renderAttributes;

      return container;
    },

    onUpdate({ elInput, component }) {
      if (elInput && typeof (elInput as any).renderAttributes === "function") {
        const savedAttributes: { key: string; value: string }[] =
          component.get("customAttributes") || [];
        (elInput as any).renderAttributes(savedAttributes);
      }
    },

    onEvent({ elInput, component }) {
      const attributes: { key: string; value: string }[] = [];
      elInput.querySelectorAll(".custom-attr-row").forEach((row) => {
        const key = (
          row.querySelector(".attr-key") as HTMLInputElement
        )?.value.trim();
        const value = (
          row.querySelector(".attr-value") as HTMLInputElement
        )?.value.trim();
        if (key) attributes.push({ key, value });
      });
      component.set("customAttributes", attributes);
      const existingAttributes = component.getAttributes();
      const customAttributes = Object.fromEntries(
        attributes.map(({ key, value }) => [key, value])
      );
      component.setAttributes({ ...existingAttributes, ...customAttributes });
    },
  });

  editor.on("component:selected", (component) => {
    if (component) {
      component.addTrait({
        type: "custom-attributes",
        name: "custom-attributes",
        label: "Custom Attributes",
      });

      const savedAttributes: { key: string; value: string }[] =
        component.get("customAttributes") || [];
      const existingAttributes = component.getAttributes();
      const customAttributes = Object.fromEntries(
        savedAttributes.map(({ key, value }) => [key, value])
      );
      component.setAttributes({ ...existingAttributes, ...customAttributes });
    }
  });
};
